<template>
  <div>
    <contact-list-select @selection-changed="onSelectionChanged" />
    <div v-if="campaignId || teamId">
      <div
        class="tw-pt-[36px] md:tw-pt-[58px] tw-pb-[16px] md:tw-rounded-[20px] md:tw-border-[1px] md:tw-border-[var(--my-primary)] md:tw-bg-[var(--my-white)]"
      >
        <contact-list-header :is-loading="storeLoading" />

        <div class="tw-mt-[36px] tw-mb-[46px] md:tw-ps-[33px] md:tw-pe-[40px]">
          <contact-list-search />
        </div>

        <div
          class="tw-pb-[16px] md:tw-pb-0 tw-rounded-[20px] tw-overflow-hidden md:tw-overflow-visible tw-shadow-[0px_2px_20px_rgba(0,0,0,0.05)] md:tw-shadow-none tw-bg-[var(--my-white)]"
        >
          <template v-if="storeError">
            <div class="tw-text-red-500 tw-p-4 tw-text-center">{{ storeError }}</div>
          </template>
          <template v-else-if="storeLoading">
            <b-skeleton class="tw-mx-[16px] md:tw-mx-[22px]" height="826px" />
          </template>
          <template v-else>
            <template v-if="contacts.length === 0">
              <div class="tw-p-4 tw-text-center">
                {{ $t("donor_dashboard.my_contacts_no_contacts_found", "No contacts found") }}
              </div>
            </template>
            <template v-else>
              <contact-list-item
                header
                :item="{
                  name: $t('donor_dashboard.my_contacts_item_table_header_name', 'Contact'),
                  status: $t('donor_dashboard.my_contacts_item_table_header_status', 'Status'),
                  lastAction: $t(
                    'donor_dashboard.my_contacts_item_table_header_last_action',
                    'Last action'
                  ),
                  team: $t('donor_dashboard.my_contacts_item_table_header_team', 'Team'),
                  parentTeam: $t(
                    'donor_dashboard.my_contacts_item_table_header_parent_team',
                    'Parent team'
                  ),
                  gParentTeam: $t(
                    'donor_dashboard.my_contacts_item_table_header_g_parent_team',
                    'G-parent team'
                  ),
                }"
                class="first tw-px-[16px] md:tw-px-[22px] tw-border-none"
              />
              <contact-list-item
                v-for="(item, index) in contacts"
                :key="item.id"
                :item="item"
                class="tw-px-[16px] md:tw-px-[22px]"
                :class="{ first: index === 0 }"
              />
            </template>
          </template>
        </div>
        <b-skeleton
          class="tw-flex tw-gap-[8px] tw-justify-center tw-items-center tw-my-[16px] tw-mx-[16px] md:tw-mx-[22px]"
          v-if="storeLoading"
          height="32px"
        />
        <my-pagination
          v-else-if="storePagination.total > storePagination.per_page"
          :value="storePagination.page"
          @input="onPageChange"
          :total-rows="storePagination.total"
          :per-page="storePagination.per_page"
        />
      </div>

      <b-skeleton v-if="storeLoading" height="728px" />
    </div>
    <div v-else class="tw-p-4 tw-text-[var(--my-primary)]">
      {{
        $t(
          "donor_dashboard.my_contacts_no_campaign_or_team_selected",
          "No campaign or team selected. Campaigns and teams are required to view contacts."
        )
      }}
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { useTeamLeaderContacts } from "@/composables/useDashboardTeamLeaderContacts";
import contactListItem from "@/views/dashboard/my-contacts/contact-list/contact-list-item/contact-list-item.vue";
import contactListHeader from "@/views/dashboard/my-contacts/contact-list/contact-list-header.vue";
import contactListSearch from "@/views/dashboard/my-contacts/contact-list/contact-list-search.vue";
import { notificationsMixin } from "@/mixins";
import ContactListSelect from "@/views/dashboard/my-contacts/contact-list/contact-list-select.vue";
import MyPagination from "@/views/my-contacts/ui/my-pagination.vue";

export default defineComponent({
  mixins: [notificationsMixin],
  components: {
    contactListItem,
    contactListHeader,
    contactListSearch,
    ContactListSelect,
    MyPagination,
  },
  setup() {
    const {
      contacts,
      storeLoading,
      storeError,
      fetchContacts,
      campaigns,
      teams,
      campaignId,
      teamId,
      storePagination,
      storeCampaignCurrency,
      storeSearchQuery,
      storeSearchFilters,
      getSearchFiltersURLParams,
      resetStorePagination,
    } = useTeamLeaderContacts();

    return {
      contacts,
      storeLoading,
      storeError,
      fetchContacts,
      campaigns,
      teams,
      campaignId,
      teamId,
      storePagination,
      storeCampaignCurrency,
      storeSearchQuery,
      storeSearchFilters,
      getSearchFiltersURLParams,
      resetStorePagination,
    };
  },

  watch: {
    storeSearchQuery(value) {
      this.storePagination = {
        ...this.storePagination,
        page: 1,
      };

      this.fetchContacts({
        campaign_id: this.campaignId,
        team_id: this.teamId,
        search: value,
        ...this.getSearchFiltersURLParams(this.storeSearchFilters),
      });
    },
    storeSearchFilters(value) {
      const params = this.getSearchFiltersURLParams(value);

      this.storePagination = {
        ...this.storePagination,
        page: 1,
      };

      this.fetchContacts({
        campaign_id: this.campaignId,
        team_id: this.teamId,
        ...(this.storeSearchQuery ? { search: this.storeSearchQuery } : {}),
        ...(params.status.length > 0 ? { status: params.status } : {}),
      });
    },
  },
  methods: {
    async onPageChange(page) {
      // Update pagination state and fetch contacts for the selected page
      this.storePagination = {
        ...this.storePagination,
        page,
      };
      await this.fetchContacts();
    },
    async onSelectionChanged(event) {
      this.resetStorePagination();
      const params = this.getSearchFiltersURLParams(this.storeSearchFilters);
      this.campaignId = event.campaign?.id;
      this.storeCampaignCurrency = event.campaign?.currency || "$";
      this.teamId = event?.teamId;

      await this.fetchContacts({
        campaign_id: this.campaignId,
        team_id: this.teamId,
        ...(this.storeSearchQuery ? { search: this.storeSearchQuery } : {}),
        ...(params.status.length > 0 ? { status: params.status } : {}),
      });
    },
  },
});
</script>
